/**
 * BookingsWithPickers — composition-root wiring for the Bookings feature's
 * cross-feature pickers (11E.4 §D).
 *
 * Bookings cannot import @features/people or @features/catalog directly
 * (import-boundary lint rule — see eslint.config.mjs). Routes sits above
 * the feature layer, so the actual subject/student/teacher pickers and the
 * entitlement hint are composed here and injected into <Bookings> via
 * render props, mirroring the StudentDetail/StaffDetail tab-injection
 * pattern used elsewhere in this file.
 */

import React, { useMemo, useState } from 'react';
import { Bookings } from '@/features/bookings';
import { Dropdown } from '@/components/common';
import { useSubjects } from '@features/catalog/api/subjects';
import { useStudentEntitlements } from '@features/catalog/api/entitlements';
import { useStaffList } from '@features/people/api/staff';
import { useStudentsList } from '@features/people/api/students';

const SubjectPicker: React.FC<{ value: string; onChange: (v: string) => void }> = ({
  value,
  onChange,
}) => {
  const { data } = useSubjects({ status: 'active' });
  const options = useMemo(
    () => [
      { value: '', label: 'Select a subject…' },
      ...(data?.items ?? []).map((s) => ({ value: s.id, label: s.name })),
    ],
    [data],
  );
  return (
    <Dropdown
      options={options}
      value={value}
      onChange={onChange}
      testId="candeza-bookings-create-subject-select"
    />
  );
};

const TeacherPicker: React.FC<{ value: string; onChange: (v: string) => void }> = ({
  value,
  onChange,
}) => {
  // Filter placeholder — staff_type=teacher lands in 11E.5; every active
  // staff member is offered for now.
  const { data } = useStaffList({ status: 'active', page_size: 100 });
  const options = useMemo(
    () => [
      { value: '', label: 'Unassigned' },
      ...(data?.items ?? []).map((s) => ({ value: s.id, label: `${s.first_name} ${s.last_name}` })),
    ],
    [data],
  );
  return (
    <Dropdown
      options={options}
      value={value}
      onChange={onChange}
      testId="candeza-bookings-create-teacher-select"
    />
  );
};

const StudentPicker: React.FC<{
  value: string;
  onChange: (v: string) => void;
  search: string;
  onSearchChange: (v: string) => void;
}> = ({ value, onChange, search, onSearchChange }) => {
  const { data, isLoading } = useStudentsList({
    status: 'active',
    search: search || undefined,
    page_size: 20,
  });
  const options = useMemo(
    () => [
      { value: '', label: isLoading ? 'Searching…' : 'Select a student…' },
      ...(data?.items ?? []).map((s) => ({ value: s.id, label: `${s.first_name} ${s.last_name}` })),
    ],
    [data, isLoading],
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <input
        type="text"
        data-testid="candeza-bookings-create-student-search-input"
        placeholder="Search students by name…"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13 }}
      />
      <Dropdown
        options={options}
        value={value}
        onChange={onChange}
        testId="candeza-bookings-create-student-select"
      />
    </div>
  );
};

const EntitlementHint: React.FC<{ studentId: string; subjectId: string }> = ({
  studentId,
  subjectId,
}) => {
  const { data: entitlements } = useStudentEntitlements(studentId || undefined);
  const eligible = (entitlements ?? []).filter(
    (e) => e.status === 'active' && (!subjectId || e.subject_ids.includes(subjectId)),
  );
  if (eligible.length === 0) return null;
  return (
    <div
      data-testid="candeza-bookings-create-entitlement-hint"
      style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}
    >
      Active package(s): {eligible.map((e) => e.package_snapshot.name).join(', ')}
    </div>
  );
};

export const BookingsWithPickers: React.FC = () => {
  const [studentSearch, setStudentSearch] = useState('');
  return (
    <Bookings
      renderSubjectPicker={(value, onChange) => <SubjectPicker value={value} onChange={onChange} />}
      renderTeacherPicker={(value, onChange) => <TeacherPicker value={value} onChange={onChange} />}
      renderStudentPicker={(value, onChange) => (
        <StudentPicker
          value={value}
          onChange={onChange}
          search={studentSearch}
          onSearchChange={setStudentSearch}
        />
      )}
      renderEntitlementHint={(studentId, subjectId) => (
        <EntitlementHint studentId={studentId} subjectId={subjectId} />
      )}
    />
  );
};

